import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ChevronLeft, ChevronRight, Target } from 'lucide-react';
import Button from '../ui/Button';

/**
 * Componente de tour guiado interativo
 * Mostra dicas contextuais nos elementos da interface
 */
const InteractiveTour = ({ 
  isActive, 
  onComplete, 
  onSkip, 
  steps = [], 
  autoStart = true 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [highlightPosition, setHighlightPosition] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const currentStepData = steps[currentStep];

  useEffect(() => {
    if (isActive && autoStart) {
      setIsVisible(true);
      if (currentStepData) {
        highlightElement(currentStepData.selector);
      }
    }
  }, [isActive, currentStep, currentStepData, autoStart]);

  const highlightElement = (selector) => {
    const element = document.querySelector(selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      setHighlightPosition({
        x: rect.left + window.scrollX,
        y: rect.top + window.scrollY,
        width: rect.width,
        height: rect.height
      });
      
      // Scroll para o elemento se necessário
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'center'
      });
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = () => {
    setIsVisible(false);
    onComplete?.();
  };

  const skipTour = () => {
    setIsVisible(false);
    onSkip?.();
  };

  if (!isActive || !isVisible || !currentStepData) {
    return null;
  }

  return (
    <>
      {/* Overlay escuro */}
      <div className="fixed inset-0 bg-black/50 z-40" />
      
      {/* Destaque do elemento */}
      <div
        className="fixed z-50 border-4 border-blue-500 rounded-lg shadow-lg pointer-events-none"
        style={{
          left: highlightPosition.x - 4,
          top: highlightPosition.y - 4,
          width: highlightPosition.width + 8,
          height: highlightPosition.height + 8,
          transition: 'all 0.3s ease'
        }}
      >
        {/* Pulse animation */}
        <div className="absolute inset-0 border-4 border-blue-400 rounded-lg animate-ping opacity-75" />
      </div>

      {/* Tooltip com informações */}
      <div
        className="fixed z-50 bg-white rounded-xl shadow-2xl max-w-sm w-80 p-6"
        style={{
          left: Math.max(20, Math.min(
            window.innerWidth - 340,
            highlightPosition.x + highlightPosition.width + 20
          )),
          top: Math.max(20, highlightPosition.y)
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Target className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{currentStepData.title}</h3>
              <p className="text-xs text-gray-500">
                Passo {currentStep + 1} de {steps.length}
              </p>
            </div>
          </div>
          <button
            onClick={skipTour}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 leading-relaxed">
            {currentStepData.content}
          </p>
          
          {currentStepData.tip && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-800">
                💡 <strong>Dica:</strong> {currentStepData.tip}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStep ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          <div className="flex items-center space-x-2">
            {currentStep > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={prevStep}
                className="flex items-center"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
            )}
            
            <Button
              size="sm"
              onClick={nextStep}
              className="flex items-center"
            >
              {currentStep < steps.length - 1 ? (
                <>
                  Próximo
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Finalizar
                  <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

// Hook para gerenciar tours interativos
export const useInteractiveTour = (tourId) => {
  const [isActive, setIsActive] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(`tour_${tourId}_completed`);
    const skipped = localStorage.getItem(`tour_${tourId}_skipped`);
    
    setHasCompletedTour(!!completed || !!skipped);
    
    // Auto-iniciar tour se não foi completado e há delay
    if (!completed && !skipped) {
      const timer = setTimeout(() => {
        setIsActive(true);
      }, 2000); // 2 segundos de delay
      
      return () => clearTimeout(timer);
    }
  }, [tourId]);

  const startTour = () => {
    setIsActive(true);
  };

  const completeTour = () => {
    localStorage.setItem(`tour_${tourId}_completed`, 'true');
    localStorage.setItem(`tour_${tourId}_date`, new Date().toISOString());
    setIsActive(false);
    setHasCompletedTour(true);
  };

  const skipTour = () => {
    localStorage.setItem(`tour_${tourId}_skipped`, 'true');
    localStorage.setItem(`tour_${tourId}_date`, new Date().toISOString());
    setIsActive(false);
    setHasCompletedTour(true);
  };

  const resetTour = () => {
    localStorage.removeItem(`tour_${tourId}_completed`);
    localStorage.removeItem(`tour_${tourId}_skipped`);
    localStorage.removeItem(`tour_${tourId}_date`);
    setHasCompletedTour(false);
  };

  return {
    isActive,
    hasCompletedTour,
    startTour,
    completeTour,
    skipTour,
    resetTour
  };
};

export default InteractiveTour;
